<template>
  <div
    role="main"
    aria-label="Détails du fournisseur logistique"
  >
    <!-- Loading State -->
    <b-overlay
      :show="isLoading"
      spinner-variant="primary"
      rounded
    >
      <!-- Error Alert -->
      <b-alert
        v-if="error"
        variant="danger"
        show
      >
        {{ error }}
      </b-alert>

      <div v-if="supplier">
        <span
          id="supplier-details-desc"
          class="sr-only"
        >
          Page détaillée d’un fournisseur logistique. Consultez les informations professionnelles, coordonnées, tarification et statistiques. Utilisez les onglets pour naviguer entre les sections.
        </span>
        <!-- Supplier Header with Status and Actions -->
        <b-card
          class="mb-4"
          aria-label="En-tête fournisseur et actions"
        >
          <b-row>
            <b-col
              cols="12"
              md="8"
            >
              <!-- Supplier Identity -->
              <div class="d-flex align-items-center">
                <b-avatar
                  :text="getInitials(supplier.username)"
                  variant="light-primary"
                  size="lg"
                  class="mr-3"
                  aria-label="Initiales du fournisseur"
                />
                <div>
                  <h2
                    id="supplier-name"
                    class="mb-1"
                  >
                    {{ supplier.username }}
                  </h2>
                  <div
                    class="d-flex flex-wrap"
                    aria-label="Coordonnées du fournisseur"
                  >
                    <div class="mr-3 mb-1">
                      <feather-icon
                        icon="AtSignIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Adresse e-mail">{{ supplier.email }}</span>
                    </div>
                    <div class="mr-3 mb-1">
                      <feather-icon
                        icon="PhoneIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Téléphone principal">{{ supplier.phone }}</span>
                    </div>
                    <div
                      v-if="supplier.phone2"
                      class="mr-3 mb-1"
                    >
                      <feather-icon
                        icon="PhoneCallIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Téléphone secondaire">{{ supplier.phone2 }}</span>
                    </div>
                  </div>
                  <div
                    class="mt-1"
                    aria-label="Statut et tarification du fournisseur"
                  >
                    <b-badge
                      :variant="supplier.is_active ? 'light-success' : 'light-danger'"
                      class="mr-1"
                      aria-label="Statut du compte"
                    >
                      {{ supplier.is_active ? 'Actif' : 'Inactif' }}
                    </b-badge>
                    <b-badge
                      v-if="supplier.weight_price"
                      variant="light-primary"
                      aria-label="Tarification par poids activée"
                    >
                      Prix par poids
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="d-flex justify-content-md-end mt-2 mt-md-0"
            >
              <!-- Action Buttons -->
              <div class="supplier-actions">
                <b-button-group class="mb-2 mb-md-0 mr-md-2">
                  <b-button
                    v-if="user && (user.role === 'admin' || user.role === 'sales')"
                    v-b-tooltip.hover
                    title="Modifier"
                    variant="outline-primary"
                    :to="{ name: 'supplier-edit', params: { id: supplier.id }}"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="16"
                      class="mr-1"
                    />
                    <span class="d-none d-md-inline">Modifier</span>
                  </b-button>

                  <b-button
                    v-if="user && user.role === 'admin'"
                    v-b-tooltip.hover
                    title="Changer le mot de passe"
                    variant="outline-secondary"
                    :to="{ name: 'supplier-change-password', params: { id: supplier.id }}"
                  >
                    <feather-icon
                      icon="LockIcon"
                      size="16"
                      class="mr-1"
                    />
                    <span class="d-none d-md-inline">Mot de passe</span>
                  </b-button>
                </b-button-group>

                <!-- Fix the toggle status button by properly implementing toggleSupplierStatus method -->
                <b-button
                  v-if="user && user.role === 'admin'"
                  variant="outline-danger"
                  class="mb-75"
                  @click="toggleSupplierStatus"
                >
                  <feather-icon
                    icon="PowerIcon"
                    class="mr-50"
                    size="16"
                  />
                  <span>{{ supplier.is_active ? 'Désactiver le fournisseur' : 'Activer le fournisseur' }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- KPI Overview Cards -->
        <b-row class="mb-4">
          <!-- Total Orders KPI -->
          <b-col
            lg="3"
            md="6"
            sm="12"
            class="mb-3"
          >
            <b-card
              no-body
              class="kpi-card border-left-primary"
            >
              <b-card-body class="py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-muted mb-1">
                      Total Commandes
                    </h6>
                    <h4 class="mb-0">
                      {{ supplier.total_orders_count }}
                    </h4>
                  </div>
                  <div class="avatar bg-light-primary p-2">
                    <feather-icon
                      icon="PackageIcon"
                      class="text-primary"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Revenue KPI -->
          <b-col
            lg="3"
            md="6"
            sm="12"
            class="mb-3"
          >
            <b-card
              no-body
              class="kpi-card border-left-success"
            >
              <b-card-body class="py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-muted mb-1">
                      Chiffre d'affaires
                    </h6>
                    <h4 class="mb-0">
                      {{ formatPrice(supplier.total_revenue) }}
                    </h4>
                  </div>
                  <div class="avatar bg-light-success p-2">
                    <feather-icon
                      icon="TrendingUpIcon"
                      class="text-success"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Success Rate KPI -->
          <b-col
            lg="3"
            md="6"
            sm="12"
            class="mb-3"
          >
            <b-card
              no-body
              class="kpi-card border-left-info"
            >
              <b-card-body class="py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-muted mb-1">
                      Taux de livraison
                    </h6>
                    <h4 class="mb-0">
                      {{ supplier.delivery_success_rate }}%
                    </h4>
                  </div>
                  <div class="avatar bg-light-info p-2">
                    <feather-icon
                      icon="PercentIcon"
                      class="text-info"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Active Orders KPI -->
          <b-col
            lg="3"
            md="6"
            sm="12"
            class="mb-3"
          >
            <b-card
              no-body
              class="kpi-card border-left-warning"
            >
              <b-card-body class="py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-muted mb-1">
                      Commandes actives
                    </h6>
                    <h4 class="mb-0">
                      {{ supplier.active_orders_count }}
                    </h4>
                  </div>
                  <div class="avatar bg-light-warning p-2">
                    <feather-icon
                      icon="TruckIcon"
                      class="text-warning"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <!-- Main Content Tabs -->
        <b-card no-body>
          <b-tabs
            pills
            card
            aria-label="Navigation des sections du fournisseur"
            aria-describedby="supplier-details-desc"
          >
            <!-- General Information Tab -->
            <b-tab
              title="Informations Générales"
              active
              aria-label="Informations générales du fournisseur"
            >
              <b-row>
                <!-- Contact Information -->
                <b-col lg="6">
                  <b-card
                    title="Informations de contact"
                    class="mb-4"
                    aria-label="Informations de contact du fournisseur"
                  >
                    <b-list-group flush>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Email</strong>
                        <span>{{ supplier.email }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Téléphone Principal</strong>
                        <span>{{ supplier.phone || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Téléphone Secondaire</strong>
                        <span>{{ supplier.phone2 || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Adresse</strong>
                        <span>{{ supplier.address || 'Non spécifié' }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-card>
                </b-col>

                <!-- Business Information -->
                <b-col lg="6">
                  <b-card
                    title="Informations commerciales"
                    class="mb-4"
                    aria-label="Informations commerciales du fournisseur"
                  >
                    <b-list-group flush>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Matricule Fiscal</strong>
                        <span>{{ supplier.tax_identification_number || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Dépôt</strong>
                        <span v-if="supplier.repository">{{ supplier.repository.name }}</span>
                        <span v-else>Non assigné</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Prix de livraison</strong>
                        <span>{{ formatPrice(supplier.price_delivery) }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Prix de retour</strong>
                        <span>{{ formatPrice(supplier.price_return) }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-card>
                </b-col>
              </b-row>

              <!-- Weight Pricing Section -->
              <b-card
                v-if="supplier.weight_price"
                title="Tarification par poids"
                class="mb-4"
                aria-label="Tableau de tarification par poids"
              >
                <div v-if="supplier.prices && supplier.prices.length > 0">
                  <b-table
                    :items="supplier.prices"
                    striped
                    responsive
                    class="weight-pricing-table"
                    :fields="[
                      { key: 'weight.weight', label: 'Catégorie de poids' },
                      { key: 'weight.min', label: 'Poids min' },
                      { key: 'weight.max', label: 'Poids max' },
                      { key: 'weight.unit', label: 'Unité' },
                      { key: 'price_delivery', label: 'Prix livraison (TND)', thClass: 'price-column' },
                      { key: 'price_return', label: 'Prix retour (TND)', thClass: 'price-column' }
                    ]"
                    aria-label="Tableau des prix par catégorie de poids"
                    aria-describedby="weight-pricing-desc"
                  >
                    <template slot="table-caption">
                      <span
                        id="weight-pricing-desc"
                        class="sr-only"
                      >
                        Tableau listant les prix de livraison et de retour selon les catégories de poids définies pour ce fournisseur logistique.
                      </span>
                    </template>
                    <template #cell(weight.weight)="data">
                      <strong class="text-primary weight-category">{{ data.item.weight.weight }}</strong>
                    </template>
                    <template #cell(weight.min)="data">
                      <span v-if="data.item.weight.min">{{ data.item.weight.min }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(weight.max)="data">
                      <span v-if="data.item.weight.max">{{ data.item.weight.max }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(weight.unit)="data">
                      <b-badge variant="light-primary">
                        {{ data.item.weight.unit }}
                      </b-badge>
                    </template>
                    <template #cell(price_delivery)="data">
                      <strong class="text-success">{{ formatPrice(data.item.price_delivery) }}</strong>
                    </template>
                    <template #cell(price_return)="data">
                      <strong class="text-warning">{{ formatPrice(data.item.price_return) }}</strong>
                    </template>
                  </b-table>
                </div>
                <div
                  v-else-if="supplier.weights && supplier.weights.length > 0"
                  class="text-center py-3"
                >
                  <b-alert
                    show
                    variant="warning"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      class="mr-1"
                    />
                    Ce fournisseur utilise la tarification par poids mais aucun prix spécifique n'est configuré.
                  </b-alert>
                  <b-table
                    :items="supplier.weights"
                    striped
                    responsive
                    :fields="[
                      { key: 'weight', label: 'Catégorie de poids' },
                      { key: 'min', label: 'Poids min' },
                      { key: 'max', label: 'Poids max' },
                      { key: 'unit', label: 'Unité' }
                    ]"
                  >
                    <template #cell(weight)="data">
                      <strong class="text-primary">{{ data.item.weight }}</strong>
                    </template>
                    <template #cell(min)="data">
                      <span v-if="data.item.min">{{ data.item.min }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(max)="data">
                      <span v-if="data.item.max">{{ data.item.max }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(unit)="data">
                      <b-badge variant="light-primary">
                        {{ data.item.unit }}
                      </b-badge>
                    </template>
                  </b-table>
                </div>
                <div
                  v-else
                  class="text-center py-3"
                >
                  <b-alert
                    show
                    variant="info"
                  >
                    <feather-icon
                      icon="InfoIcon"
                      class="mr-1"
                    />
                    Aucune catégorie de poids configurée pour ce fournisseur.
                  </b-alert>
                </div>
              </b-card>
              <b-alert
                v-else
                show
                variant="info"
              >
                <feather-icon
                  icon="InfoIcon"
                  class="mr-1"
                />
                Ce fournisseur n'utilise pas la tarification par poids
              </b-alert>
            </b-tab>

            <!-- Statistics Tab -->
            <b-tab title="Statistiques">
              <b-row>
                <b-col
                  md="6"
                  class="mb-4"
                >
                  <b-card title="Performance de livraison">
                    <b-row>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-primary rounded">
                          <h6 class="text-muted mb-1">
                            Commandes Livrées
                          </h6>
                          <h4 class="mb-0">
                            {{ supplier.delivered_orders_count }}
                          </h4>
                        </div>
                      </b-col>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-danger rounded">
                          <h6 class="text-muted mb-1">
                            Commandes Retournées
                          </h6>
                          <h4 class="mb-0">
                            {{ supplier.returned_orders_count }}
                          </h4>
                        </div>
                      </b-col>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-info rounded">
                          <h6 class="text-muted mb-1">
                            Taux de livraison
                          </h6>
                          <h4 class="mb-0">
                            {{ supplier.delivery_success_rate }}%
                          </h4>
                        </div>
                      </b-col>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-warning rounded">
                          <h6 class="text-muted mb-1">
                            Délai moyen (jours)
                          </h6>
                          <h4 class="mb-0">
                            {{ supplier.average_delivery_time || 'N/A' }}
                          </h4>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>

                <b-col
                  md="6"
                  class="mb-4"
                >
                  <b-card title="Statistiques financières">
                    <b-row>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-success rounded">
                          <h6 class="text-muted mb-1">
                            Chiffre d'affaires
                          </h6>
                          <h4 class="mb-0">
                            {{ formatPrice(supplier.total_revenue) }}
                          </h4>
                        </div>
                      </b-col>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-primary rounded">
                          <h6 class="text-muted mb-1">
                            Frais de livraison
                          </h6>
                          <h4 class="mb-0">
                            {{ formatPrice(supplier.total_delivery_fees) }}
                          </h4>
                        </div>
                      </b-col>
                      <b-col
                        md="6"
                        class="mb-3"
                      >
                        <div class="stat-card p-3 bg-light-info rounded">
                          <h6 class="text-muted mb-1">
                            Valeur moyenne
                          </h6>
                          <h4 class="mb-0">
                            {{ formatPrice(supplier.average_order_value) }}
                          </h4>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Orders Tab -->
            <b-tab title="Commandes">
              <div class="text-center py-3">
                <feather-icon
                  icon="PackageIcon"
                  size="32"
                  class="mb-1 text-muted"
                />
                <h5 class="mb-1">
                  Historique des commandes
                </h5>
                <p class="mb-2 text-muted">
                  Visualisez toutes les commandes de ce fournisseur
                </p>
                <b-button
                  variant="primary"
                  :to="{ name: 'order-list', params: { level: 0 }, query: { supplier: supplier.id } }"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="16"
                    class="mr-1"
                  />
                  Voir les commandes
                </b-button>
              </div>
            </b-tab>

            <!-- Invoice Generation Days Tab -->
            <b-tab
              v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
              title="Planification de génération de facture"
            >
              <invoice-generation-days :supplier-id="supplier.id" />
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </b-overlay>
  </div>
</template>

<script>
// Make sure ToastificationContent is properly imported
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import {
  BCard,
  BRow,
  BCol,
  BOverlay,
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BButtonGroup,
  BListGroup,
  BListGroupItem,
  BTabs,
  BTab,
  BTable,
} from 'bootstrap-vue'
import InvoiceGenerationDays from './components/InvoiceGenerationDays.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BOverlay,
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BButtonGroup,
    BListGroup,
    BListGroupItem,
    BTabs,
    BTab,
    BTable,
    InvoiceGenerationDays,
  },

  data() {
    return {
      isLoading: false,
      supplier: null,
      error: null,
      user: null,
    }
  },

  created() {
    this.user = storeAuth.state.user
    this.fetchSupplierDetails()
  },

  methods: {
    async fetchSupplierDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        // Use the new dedicated supplier endpoint (non-paginated)
        const { data } = await axios.get(`/api/auth/users/suppliers/${id}/`)
        // Direct assignment, no .results or pagination
        this.supplier = data
      } catch (error) {
        console.error('Error fetching supplier details:', error)
        this.error = 'Erreur lors du chargement des informations du fournisseur'
      } finally {
        this.isLoading = false
      }
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    formatPrice(price) {
      if (price === null || price === undefined) return 'Non spécifié'
      return `${parseFloat(price).toFixed(3)} DT`
    },

    // Fix the toggleSupplierStatus method
    async toggleSupplierStatus() {
      const result = await this.$swal({
        title: this.supplier.is_active ? 'Désactiver le fournisseur?' : 'Activer le fournisseur?',
        text: this.supplier.is_active
          ? 'Le fournisseur ne pourra plus se connecter ni être sélectionné pour les commandes.'
          : 'Le fournisseur pourra à nouveau se connecter et être sélectionné pour les commandes.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, confirmer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })

      // Check if the user confirmed
      if (result.value) {
        try {
          this.isLoading = true

          // Make API call to toggle status
          const { data } = await axios.post(`/api/auth/users/toggle-activation/${this.supplier.id}/`)
          // Update supplier data with new status from response
          this.supplier.is_active = data.is_active

          this.showToast(
            'success',
            'top-right',
            `Le fournisseur a été ${this.supplier.is_active ? 'activé' : 'désactivé'} avec succès`,
          )
        } catch (error) {
          console.error('Error toggling supplier status:', error)
          this.showToast(
            'danger',
            'top-right',
            'Erreur lors du changement de statut du fournisseur',
          )
        } finally {
          this.isLoading = false
        }
      }
    },

    // Add the showToast method
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style scoped>
.supplier-actions {
  display: flex;
  align-items: center;
}
.stat-card {
  border-radius: 0.375rem;
}

/* Weight pricing table styling */
.table th {
  border-top: none;
  font-weight: 600;
  color: #6e6b7b;
}

.weight-pricing-table {
  margin-bottom: 0;
}

.price-column {
  font-weight: 600;
}

.weight-category {
  font-size: 0.95rem;
}
</style>
